import { useEffect, useState } from 'react'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { useLocalStorage } from 'usehooks-ts'

import SafeLocalStorage from 'modules/core/lib/SafeLocalStorage'

interface DismissalState {
  dismissCount: number
  lastDismissedAt: string | null
}

const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000

export const useDismissalState = (
  moduleIdentifier: string,
  isUpsellingVisible: boolean,
  requestSource: string
) => {
  const storageKey = LOCAL_STORAGE_KEYS.upsellingKitDismissalState(moduleIdentifier, requestSource)
  const [dismissalState, setDismissalState] = useLocalStorage<DismissalState>(storageKey, {
    dismissCount: 0,
    lastDismissedAt: null,
  })

  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    const checkVisibility = () => {
      // If upselling is not visible (module already installed, module not available for the country, not allowed to upsell), hide the widget
      if (!isUpsellingVisible) {
        setIsVisible(false)
        return
      }

      const now = new Date().getTime()
      const lastDismissed = dismissalState.lastDismissedAt
        ? new Date(dismissalState.lastDismissedAt).getTime()
        : 0

      // If the widget has been dismissed more than once, hide it
      if (dismissalState.dismissCount >= 2) {
        setIsVisible(false)
      } else if (dismissalState.dismissCount === 1 && lastDismissed) {
        // If the widget has been dismissed once, check if the time since the last dismissal is greater than two weeks
        const timeSinceLastDismiss = now - lastDismissed
        setIsVisible(timeSinceLastDismiss >= TWO_WEEKS_MS)
      } else {
        // If the widget has not been dismissed, show it
        setIsVisible(true)
      }
    }

    checkVisibility()
  }, [dismissalState, isUpsellingVisible])

  const handleDismiss = () => {
    const newState = {
      dismissCount: dismissalState.dismissCount + 1,
      lastDismissedAt: new Date().toISOString(),
    }
    setDismissalState(newState)
    SafeLocalStorage.setItem(storageKey, JSON.stringify(newState))
  }

  return {
    isVisible,
    handleDismiss,
    dismissCount: dismissalState.dismissCount,
    setIsVisible,
  }
}
